import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import { useVolunteer } from '../../contexts/VolunteerContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';
import { ROUTES } from '../../utils/constants';

const VolunteerHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, myShifts, loading, error, fetchProfile, fetchMyShifts } = useVolunteer();

  useEffect(() => {
    fetchProfile();
    fetchMyShifts(true);
  }, [fetchProfile, fetchMyShifts]);

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const upcomingShifts = myShifts
    .filter((s) => s.status === 'confirmed' && s.shift && new Date(s.shift.date) >= todayStart)
    .slice(0, 5);

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Welcome back, {user?.firstName}!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Your volunteer dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {loading && !profile ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AccessTimeIcon sx={{ fontSize: 40, color: '#00b7ed', mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {profile?.totalHours ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Hours
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AssignmentIcon sx={{ fontSize: 40, color: '#00b7ed', mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {profile?.totalShifts ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Shifts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <EventIcon sx={{ fontSize: 40, color: '#00b7ed', mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {profile?.upcomingShifts ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upcoming Shifts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<EventIcon />}
              onClick={() => navigate(ROUTES.VOLUNTEER_SHIFTS)}
              sx={{ backgroundColor: '#00b7ed', '&:hover': { backgroundColor: '#009fcc' } }}
            >
              Browse Shifts
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate(ROUTES.VOLUNTEER_MY_SHIFTS)}
            >
              My Shifts
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => navigate(ROUTES.VOLUNTEER_PROFILE)}
            >
              My Profile
            </Button>
          </Box>

          {/* Upcoming Shifts List */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Upcoming Shifts
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : upcomingShifts.length === 0 ? (
            <Alert severity="info">
              You have no upcoming shifts.{' '}
              <Button
                size="small"
                onClick={() => navigate(ROUTES.VOLUNTEER_SHIFTS)}
                sx={{ ml: 1 }}
              >
                Browse Available Shifts
              </Button>
            </Alert>
          ) : (
            <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
              {upcomingShifts.map((registration, index) => (
                <ListItem
                  key={registration.id}
                  sx={{
                    borderBottom:
                      index < upcomingShifts.length - 1 ? '1px solid #eee' : 'none',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                  onClick={() =>
                    navigate(
                      ROUTES.VOLUNTEER_SHIFT_DETAIL.replace(':id', registration.shiftId)
                    )
                  }
                >
                  <ListItemText
                    primary={
                      <Typography fontWeight={500}>
                        {registration.shift?.title}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EventIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {registration.shift?.date
                              ? formatDate(registration.shift.date)
                              : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {registration.shift?.startTime} - {registration.shift?.endTime}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOnIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {registration.shift?.location}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  {registration.role && (
                    <Chip label={registration.role.name} size="small" sx={{ ml: 1 }} />
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}
    </Box>
  );
};

export default VolunteerHomePage;
